/**
 * RealVsBudgetTrajectory - Painel executivo reformulado
 * 
 * NOVA LÓGICA:
 * - 2026: Gráfico = Rolling 12 meses REAL | Bloco separado = 1T26 (destacado) + 2T/3T/4T Orçamento
 * - 2025: Gráfico = Evolução real do ano | Sem bloco de orçamento futuro
 * 
 * FUNCIONALIDADES:
 * - Selector de KPI para escolher entre os KPIs da página
 * - 1T26 com cor diferente (azul) destacado no bloco de referência
 * - Tamanhos ajustados para caber todos os trimestres
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { 
  Target, TrendingUp, TrendingDown, Minus, Calendar,
  ArrowRight, CheckCircle, AlertTriangle, ChevronDown
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  ReferenceLine, CartesianGrid
} from 'recharts';

// ============ DESIGN TOKENS ============
const colors = {
  real: {
    line: '#3B82F6',
    area: 'rgba(59, 130, 246, 0.15)',
    dot: '#2563EB',
    text: '#1E40AF',
    bg: '#DBEAFE',
  },
  budget: {
    bg: '#FEF3C7',
    border: '#F59E0B',
    text: '#92400E',
    accent: '#C5A059',
  },
  grid: 'rgba(148, 163, 184, 0.1)',
  axis: '#94A3B8',
};

// ============ CUSTOM TOOLTIP ============
const CustomTooltip = ({ active, payload, label, unit, language, showPriorYear }) => {
  if (active && payload && payload.length) {
    const value = payload.find(p => p.dataKey === 'value')?.value;
    const priorYearValue = payload.find(p => p.dataKey === 'priorYear')?.value;
    
    return (
      <div className="bg-[#0A192F] border border-white/20 rounded-lg px-3 py-2 shadow-xl">
        <p className="text-[10px] text-slate-400 font-medium mb-1">{label}</p>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.real.line }} />
            <span className="text-[9px] text-slate-400">{showPriorYear ? '2025:' : ''}</span>
            <span className="text-sm text-white font-bold">
              {typeof value === 'number' ? value.toLocaleString('pt-PT') : value}
            </span>
            <span className="text-[10px] text-slate-400">{unit}</span>
          </div>
          {showPriorYear && priorYearValue !== null && priorYearValue !== undefined && (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-slate-400" />
              <span className="text-[9px] text-slate-400">2024:</span>
              <span className="text-sm text-slate-300 font-medium">
                {typeof priorYearValue === 'number' ? priorYearValue.toLocaleString('pt-PT') : priorYearValue}
              </span>
              <span className="text-[10px] text-slate-400">{unit}</span>
            </div>
          )}
        </div>
      </div>
    );
  }
  return null;
};

// ============ TREND INDICATOR ============
const TrendIndicator = ({ trend, language }) => {
  const configs = {
    up: { icon: TrendingUp, color: 'text-emerald-500', bg: 'bg-emerald-50', label: { pt: 'Ascendente', en: 'Upward' } },
    down: { icon: TrendingDown, color: 'text-rose-500', bg: 'bg-rose-50', label: { pt: 'Descendente', en: 'Downward' } },
    stable: { icon: Minus, color: 'text-slate-500', bg: 'bg-slate-50', label: { pt: 'Estável', en: 'Stable' } },
  };
  const config = configs[trend] || configs.stable;
  const Icon = config.icon;
  
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${config.bg}`}>
      <Icon className={`w-3 h-3 ${config.color}`} />
      <span className={`text-[9px] font-medium ${config.color}`}>{config.label[language]}</span>
    </div>
  );
};

// ============ QUARTER CARD - Compacto ============
const QuarterCard = ({ quarter, value, unit, isQ1, language }) => {
  // Q1 destacado em azul, restantes em âmbar
  const bgClass = isQ1 
    ? 'bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-300' 
    : 'bg-gradient-to-br from-amber-50 to-white border-amber-200/60';
  const textClass = isQ1 ? 'text-blue-700' : 'text-amber-700';
  const valueClass = isQ1 ? 'text-blue-800' : 'text-slate-800';
  const labelClass = isQ1 ? 'text-blue-600' : 'text-slate-500';
  
  return (
    <div className={`${bgClass} border rounded-lg p-2 text-center flex-1 min-w-0`}>
      <p className={`text-[8px] font-semibold ${textClass} uppercase tracking-wider`}>
        {quarter}
      </p>
      <p className={`text-sm font-bold ${valueClass} leading-tight`}>
        {typeof value === 'number' ? value.toLocaleString('pt-PT') : value}
      </p>
      <p className={`text-[8px] ${labelClass}`}>
        {isQ1 ? (language === 'pt' ? 'Real' : 'Actual') : (language === 'pt' ? 'Orç.' : 'Bud.')}
      </p>
    </div>
  );
};

// ============ KPI SELECTOR DROPDOWN ============
const KPISelector = ({ kpis, selectedKpi, onSelect, language }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selected = kpis.find(k => k.key === selectedKpi) || kpis[0];
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 hover:border-blue-300 transition-colors"
        data-testid="kpi-selector-button"
      >
        <Target className="w-3.5 h-3.5 text-blue-500" />
        <span className="text-xs font-semibold text-slate-700">
          {typeof selected.label === 'object' ? selected.label[language] : selected.label}
        </span>
        <span className="text-[10px] text-slate-400">({selected.unit})</span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 py-1">
          {kpis.map((kpi) => (
            <button
              key={kpi.key}
              onClick={() => { onSelect(kpi.key); setIsOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 transition-colors ${
                kpi.key === selectedKpi ? 'bg-blue-50 text-blue-700' : 'text-slate-700'
              }`}
              data-testid={`kpi-option-${kpi.key}`}
            >
              <span className="font-medium">
                {typeof kpi.label === 'object' ? kpi.label[language] : kpi.label}
              </span>
              <span className="text-slate-400 ml-1">({kpi.unit})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ EXECUTIVE INSIGHT ============
const ExecutiveInsight = ({ year, recentTrend, q1VsRecent, budgetRequirement, language }) => {
  const is2026 = year === 2026;
  let icon, iconColor, message;
  
  if (is2026) {
    if (q1VsRecent >= -3 && budgetRequirement <= 5) {
      icon = CheckCircle;
      iconColor = 'text-emerald-500';
      message = { pt: 'O 1T26 está em linha com o comportamento recente. A trajectória do plano para os próximos trimestres exige ritmo semelhante.', en: 'Q1 26 is in line with recent behaviour. The plan trajectory for coming quarters requires similar pace.' };
    } else if (q1VsRecent >= -8) {
      icon = AlertTriangle;
      iconColor = 'text-amber-500';
      message = { pt: 'O 1T26 ficou ligeiramente abaixo do padrão recente. O plano dos próximos trimestres exige recuperação moderada.', en: 'Q1 26 was slightly below recent pattern. The plan for coming quarters requires moderate recovery.' };
    } else {
      icon = AlertTriangle;
      iconColor = 'text-rose-500';
      message = { pt: 'O 1T26 ficou significativamente abaixo do histórico recente. A trajectória do plano exige forte aceleração no 2T-4T.', en: 'Q1 26 was significantly below recent history. The plan trajectory requires strong acceleration in Q2-Q4.' };
    }
  } else {
    if (recentTrend === 'stable' || recentTrend === 'up') {
      icon = CheckCircle;
      iconColor = 'text-emerald-500';
      message = recentTrend === 'stable' 
        ? { pt: 'O ano de 2025 revelou trajectória operacional estável. A execução foi consistente ao longo dos doze meses.', en: 'The year 2025 revealed a stable operational trajectory. Execution was consistent throughout the twelve months.' }
        : { pt: 'O ano de 2025 mostrou trajectória de melhoria progressiva. A operação ganhou ritmo ao longo do período.', en: 'The year 2025 showed a trajectory of progressive improvement. Operations gained pace throughout the period.' };
    } else {
      icon = AlertTriangle;
      iconColor = 'text-amber-500';
      message = { pt: 'O ano de 2025 evidenciou alguma variabilidade operacional. Houve momentos de pressão que afectaram a previsibilidade.', en: 'The year 2025 showed some operational variability. There were pressure moments that affected predictability.' };
    }
  }

  const Icon = icon;
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5">
      <div className="flex items-start gap-2">
        <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${iconColor}`} />
        <p className="text-[10px] text-slate-700 leading-relaxed">{message[language]}</p>
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============
const RealVsBudgetTrajectory = ({ 
  year = 2026, 
  kpis = [],              // Array de KPIs disponíveis: [{ key, label, unit, rollingData, budgetQuarters, q1Actual, trend }]
  defaultKpi,             // KPI inicial seleccionado (key)
  // Props legacy para compatibilidade
  kpiKey,
  kpiLabel,
  unit,
  rollingData,
  budgetQuarters,
  q1Actual,
  trend = 'stable',
}) => {
  const { language } = useLanguage();
  const is2026 = year === 2026;
  
  // Se kpis array estiver vazio, usar props legacy
  const kpisList = kpis.length > 0 ? kpis : [{
    key: kpiKey || 'default',
    label: kpiLabel || { pt: 'KPI', en: 'KPI' },
    unit: unit || '',
    rollingData: rollingData || [],
    budgetQuarters: budgetQuarters || [],
    q1Actual: q1Actual,
    trend: trend,
  }];
  
  const [selectedKpiKey, setSelectedKpiKey] = useState(defaultKpi || kpisList[0]?.key);
  const [isAnimating, setIsAnimating] = useState(false);
  const selectedKpi = kpisList.find(k => k.key === selectedKpiKey) || kpisList[0];
  
  // Função para mudar KPI com animação
  const handleKpiChange = (newKpiKey) => {
    if (newKpiKey === selectedKpiKey) return;
    setIsAnimating(true);
    setTimeout(() => {
      setSelectedKpiKey(newKpiKey);
      setTimeout(() => setIsAnimating(false), 50);
    }, 150);
  };
  
  // Usar dados do KPI seleccionado
  const currentRollingData = selectedKpi?.rollingData || [];
  const currentPriorYearData = selectedKpi?.priorYearData || [];
  const currentBudgetQuarters = selectedKpi?.budgetQuarters || [];
  const currentQ1Actual = selectedKpi?.q1Actual;
  const currentTrend = selectedKpi?.trend || 'stable';
  const currentUnit = selectedKpi?.unit || '';

  // Para 2025: combinar dados de 2025 e 2024 para o gráfico
  const chartData = !is2026 && currentPriorYearData.length > 0
    ? currentRollingData.map((d, idx) => ({
        month: d.month,
        value: d.value,
        priorYear: currentPriorYearData[idx]?.value || null,
      }))
    : currentRollingData;

  // Calcular métricas para insights
  const avgRecent = currentRollingData.length > 0
    ? currentRollingData.slice(-6).reduce((sum, d) => sum + (d.value || 0), 0) / Math.min(6, currentRollingData.length)
    : 0;
  
  // Para 2025: calcular média de 2024 para comparação
  const avgPriorYear = currentPriorYearData.length > 0
    ? currentPriorYearData.reduce((sum, d) => sum + (d.value || 0), 0) / currentPriorYearData.length
    : 0;
  
  // Variação YoY para 2025
  const yoyVariation = avgPriorYear > 0 && avgRecent > 0
    ? ((avgRecent - avgPriorYear) / avgPriorYear) * 100
    : 0;
  
  const q1VsRecent = avgRecent > 0 && currentQ1Actual
    ? ((currentQ1Actual - avgRecent) / avgRecent) * 100
    : 0;

  const avgBudgetRequired = currentBudgetQuarters.length > 0
    ? currentBudgetQuarters.reduce((sum, d) => sum + (d.value || 0), 0) / currentBudgetQuarters.length
    : 0;
  
  const budgetRequirement = avgRecent > 0 && avgBudgetRequired
    ? ((avgBudgetRequired - avgRecent) / avgRecent) * 100
    : 0;

  // Títulos
  const sectionTitle = is2026
    ? { pt: 'Real Recente vs Trajectória do Orçamento 2026', en: 'Recent Actual vs 2026 Budget Trajectory' }
    : { pt: 'Evolução da Execução em 2025', en: 'Execution Evolution in 2025' };

  const subtitle = is2026
    ? { pt: 'Histórico rolling 12 meses e referência do plano', en: 'Rolling 12 months history and plan reference' }
    : { pt: 'Comparativo 2025 vs 2024', en: '2025 vs 2024 comparison' };

  // Preparar dados dos trimestres (incluindo 1T26 destacado)
  const allQuarters = is2026 && currentQ1Actual ? [
    { quarter: '1T26', value: currentQ1Actual, isQ1: true },
    ...(currentBudgetQuarters || []).map(q => ({ ...q, isQ1: false }))
  ] : [];

  return (
    <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 p-4 mb-5" data-testid="real-vs-budget-trajectory">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-300 rounded-full"></div>
          <div>
            <h3 className="text-slate-800 font-semibold text-sm">{sectionTitle[language]}</h3>
            <p className="text-slate-500 text-[9px]">{subtitle[language]}</p>
          </div>
        </div>
        
        {/* KPI Selector */}
        {kpisList.length > 1 ? (
          <KPISelector 
            kpis={kpisList} 
            selectedKpi={selectedKpiKey} 
            onSelect={handleKpiChange}
            language={language}
          />
        ) : (
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
            <Target className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-xs font-semibold text-slate-700">
              {typeof selectedKpi.label === 'object' ? selectedKpi.label[language] : selectedKpi.label}
            </span>
            <span className="text-[10px] text-slate-400">({currentUnit})</span>
          </div>
        )}
      </div>

      {/* Main Content Grid - com animação de transição */}
      <div className={`grid ${is2026 ? 'grid-cols-[1fr_220px]' : 'grid-cols-[1fr_180px]'} gap-4 transition-all duration-300 ${isAnimating ? 'opacity-0 scale-[0.98]' : 'opacity-100 scale-100'}`}>
        
        {/* LEFT: Rolling 12 Months Chart */}
        <div className="bg-white rounded-xl border border-slate-100 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Calendar className="w-3 h-3 text-slate-400" />
              <span className="text-[9px] font-medium text-slate-500 uppercase tracking-wider">
                {is2026 ? (language === 'pt' ? 'Rolling 12 Meses' : 'Rolling 12 Months') : (language === 'pt' ? '2025 vs 2024' : '2025 vs 2024')}
              </span>
            </div>
            <TrendIndicator trend={currentTrend} language={language} />
          </div>

          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorReal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={colors.real.line} stopOpacity={0.2}/>
                    <stop offset="95%" stopColor={colors.real.line} stopOpacity={0.02}/>
                  </linearGradient>
                  <linearGradient id="colorPriorYear" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#94A3B8" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#94A3B8" stopOpacity={0.02}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 8, fill: colors.axis }} axisLine={{ stroke: colors.grid }} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 8, fill: colors.axis }} axisLine={false} tickLine={false} width={35} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                <Tooltip content={<CustomTooltip unit={currentUnit} language={language} showPriorYear={!is2026 && currentPriorYearData.length > 0} />} />
                {is2026 && <ReferenceLine y={avgRecent} stroke="#94A3B8" strokeDasharray="4 4" strokeWidth={1} />}
                {/* Linha de 2024 (prior year) - apenas para 2025 */}
                {!is2026 && currentPriorYearData.length > 0 && (
                  <Area type="monotone" dataKey="priorYear" stroke="#94A3B8" strokeWidth={1.5} strokeDasharray="4 4" fill="url(#colorPriorYear)" dot={false} />
                )}
                {/* Linha principal (2025 ou rolling 12M) */}
                <Area type="monotone" dataKey="value" stroke={colors.real.line} strokeWidth={2} fill="url(#colorReal)" dot={{ fill: colors.real.dot, strokeWidth: 0, r: 1.5 }} activeDot={{ fill: colors.real.dot, strokeWidth: 2, stroke: '#fff', r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="flex items-center justify-center gap-4 mt-2 pt-2 border-t border-slate-100">
            <div className="flex items-center gap-1">
              <div className="w-3 h-0.5 bg-blue-500 rounded"></div>
              <span className="text-[8px] text-slate-500">{is2026 ? (language === 'pt' ? 'Real' : 'Actual') : '2025'}</span>
            </div>
            {is2026 ? (
              <div className="flex items-center gap-1">
                <div className="w-3 h-0.5 bg-slate-400 rounded" style={{ backgroundImage: 'repeating-linear-gradient(90deg, #94A3B8, #94A3B8 2px, transparent 2px, transparent 4px)' }}></div>
                <span className="text-[8px] text-slate-500">{language === 'pt' ? 'Média' : 'Average'}</span>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <div className="w-3 h-0.5 bg-slate-400 rounded" style={{ backgroundImage: 'repeating-linear-gradient(90deg, #94A3B8, #94A3B8 2px, transparent 2px, transparent 4px)' }}></div>
                <span className="text-[8px] text-slate-500">2024</span>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Budget Reference Block (2026) or YoY Summary (2025) */}
        {is2026 && allQuarters.length > 0 ? (
          <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 p-3">
            <div className="flex items-center gap-2 mb-2">
              <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              <div>
                <p className="text-[9px] font-semibold text-slate-700 uppercase tracking-wider">
                  {language === 'pt' ? 'Trajectória 2026' : '2026 Trajectory'}
                </p>
              </div>
            </div>

            {/* Quarter Cards - 4 em linha */}
            <div className="flex gap-1.5 mb-2">
              {allQuarters.map((q, idx) => (
                <QuarterCard 
                  key={idx}
                  quarter={q.quarter}
                  value={q.value}
                  unit={currentUnit}
                  isQ1={q.isQ1}
                  language={language}
                />
              ))}
            </div>

            {/* Comparação */}
            <div className="bg-white rounded-lg p-2 border border-slate-100">
              <div className="flex items-center justify-between">
                <span className="text-[8px] text-slate-600">
                  {language === 'pt' ? 'Plano vs Real Recente' : 'Plan vs Recent'}
                </span>
                <span className={`text-[9px] font-bold ${budgetRequirement > 5 ? 'text-amber-600' : budgetRequirement > 0 ? 'text-slate-600' : 'text-emerald-600'}`}>
                  {budgetRequirement > 0 ? '+' : ''}{budgetRequirement.toFixed(1)}%
                </span>
              </div>
              <p className="text-[8px] text-slate-500 mt-0.5">
                {budgetRequirement > 5 
                  ? (language === 'pt' ? 'Exige aceleração' : 'Requires acceleration')
                  : budgetRequirement > 0
                    ? (language === 'pt' ? 'Ritmo semelhante' : 'Similar pace')
                    : (language === 'pt' ? 'Real acima do plano' : 'Actual above plan')
                }
              </p>
            </div>
          </div>
        ) : !is2026 && currentPriorYearData.length > 0 ? (
          /* Bloco YoY Summary para 2025 */
          <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 p-3">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-3.5 h-3.5 text-slate-500" />
              <p className="text-[9px] font-semibold text-slate-700 uppercase tracking-wider">
                {language === 'pt' ? '2025 vs 2024' : '2025 vs 2024'}
              </p>
            </div>

            {/* Métricas YoY */}
            <div className="space-y-2">
              <div className="bg-white rounded-lg p-2.5 border border-slate-100">
                <p className="text-[8px] text-slate-500 mb-1">
                  {language === 'pt' ? 'Média 2025' : '2025 Average'}
                </p>
                <p className="text-lg font-bold text-slate-800">
                  {typeof avgRecent === 'number' ? avgRecent.toLocaleString('pt-PT', { maximumFractionDigits: 1 }) : avgRecent}
                  <span className="text-[9px] font-normal text-slate-400 ml-1">{currentUnit}</span>
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-2.5 border border-slate-100">
                <p className="text-[8px] text-slate-500 mb-1">
                  {language === 'pt' ? 'Média 2024' : '2024 Average'}
                </p>
                <p className="text-lg font-bold text-slate-600">
                  {typeof avgPriorYear === 'number' ? avgPriorYear.toLocaleString('pt-PT', { maximumFractionDigits: 1 }) : avgPriorYear}
                  <span className="text-[9px] font-normal text-slate-400 ml-1">{currentUnit}</span>
                </p>
              </div>

              <div className={`rounded-lg p-2.5 border ${yoyVariation > 0 ? 'bg-emerald-50 border-emerald-200' : yoyVariation < 0 ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'}`}>
                <p className="text-[8px] text-slate-500 mb-1">
                  {language === 'pt' ? 'Variação YoY' : 'YoY Variation'}
                </p>
                <p className={`text-base font-bold ${yoyVariation > 0 ? 'text-emerald-700' : yoyVariation < 0 ? 'text-rose-700' : 'text-slate-700'}`}>
                  {yoyVariation > 0 ? '+' : ''}{yoyVariation.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Executive Insight */}
      <div className="mt-3">
        <ExecutiveInsight 
          year={year}
          recentTrend={currentTrend}
          q1VsRecent={q1VsRecent}
          budgetRequirement={budgetRequirement}
          language={language}
        />
      </div>
    </div>
  );
};

export default RealVsBudgetTrajectory;
